import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Button, Card, Col, Progress, Row, Space, Spin, Steps, Tour, Typography, message } from 'antd';
import { CheckCircleOutlined, CustomerServiceOutlined, MessageOutlined, RobotOutlined, TeamOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  GET_ACCOUNT_ONBOARDING_STATUS,
  SKIP_ACCOUNT_ONBOARDING,
  SKIP_ACCOUNT_ONBOARDING_STEP
} from '../mutations/onboarding';

const { Text } = Typography;

const stepRoutes = {
  create_first_customer: '/customers',
  connect_whatsapp: '/account-settings',
  configure_ai_prompt: '/account-settings',
  configure_campaign_prompts: '/account-settings',
  configure_service_prompt: '/account-settings'
};

const stepIcons = {
  create_first_customer: <TeamOutlined />,
  connect_whatsapp: <WhatsAppOutlined />,
  configure_ai_prompt: <RobotOutlined />,
  configure_campaign_prompts: <MessageOutlined />,
  configure_service_prompt: <CustomerServiceOutlined />
};

const OnboardingProgressCard = () => {
  const navigate = useNavigate();
  const startStepButtonRef = useRef(null);
  const [messageApi, contextHolder] = message.useMessage();
  const { data, loading, error, refetch } = useQuery(GET_ACCOUNT_ONBOARDING_STATUS, { fetchPolicy: 'network-only' });
  const [skipStep, { loading: skippingStep }] = useMutation(SKIP_ACCOUNT_ONBOARDING_STEP);
  const [skipOnboarding, { loading: skippingOnboarding }] = useMutation(SKIP_ACCOUNT_ONBOARDING);

  const status = data?.accountOnboardingStatus;
  const firstPendingStep = status?.steps?.find((step) => !step.completed && !step.skipped);
  const firstPendingStepName = firstPendingStep?.step;
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    if (firstPendingStepName) {
      setTourOpen(true);
    }
  }, [firstPendingStepName]);

  if (loading) {
    return (
      <Card style={{ marginBottom: 24 }}>
        <Spin /> <Text type="secondary">Carregando primeiros passos...</Text>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert
        style={{ marginBottom: 24 }}
        type="warning"
        showIcon
        message="Não foi possível carregar o onboarding"
        description={error.message}
      />
    );
  }

  if (!status) {
    return null;
  }

  const onboardingHidden = status.steps.every((step) => step.skipped);

  if (onboardingHidden) {
    return null;
  }

  const currentStepIndex = status.steps.findIndex((step) => !step.completed && !step.skipped);
  const currentStep = firstPendingStep || null;

  const handleNavigate = (step) => {
    const route = stepRoutes[step.step] || '/dashboard';
    navigate(route, { state: { onboardingStep: step.step } });
  };

  const handleSkipStep = async (step) => {
    const response = await skipStep({ variables: { step: step.step } });
    const errors = response.data?.skipAccountOnboardingStep?.errors || [];

    if (errors.length > 0) {
      messageApi.error(errors.join(', '));
      return;
    }

    messageApi.success('Etapa ignorada');
    refetch();
  };

  const handleSkipOnboarding = async () => {
    const response = await skipOnboarding();
    const errors = response.data?.skipAccountOnboarding?.errors || [];

    if (errors.length > 0) {
      messageApi.error(errors.join(', '));
      return;
    }

    messageApi.success('Onboarding ignorado');
    refetch();
  };

  const items = status.steps.map((step) => ({
    title: step.label,
    description: step.completed ? 'Concluído' : step.skipped ? 'Ignorado' : 'Pendente',
    status: step.completed ? 'finish' : step.skipped ? 'wait' : 'process',
    icon: step.completed ? <CheckCircleOutlined /> : stepIcons[step.step]
  }));

  const tourSteps = currentStep ? [
    {
      title: 'Próxima etapa',
      description: 'Clique aqui para avançar para a próxima configuração do onboarding.',
      target: () => startStepButtonRef.current,
      placement: 'top'
    }
  ] : [];

  return (
    <Card style={{ marginBottom: 24 }}>
      {contextHolder}
      <Tour open={tourOpen && !!currentStep} steps={tourSteps} onClose={() => setTourOpen(false)} />
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} lg={8}>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Text strong>Primeiros passos</Text>
            <Text type="secondary">Configure o essencial para começar a usar o Acelera Vendas.</Text>
            <Progress percent={status.progressPercentage} status="active" />
            <Text type="secondary">
              {status.completedStepsCount} de {status.totalStepsCount} etapas concluídas
            </Text>
          </Space>
        </Col>
        <Col xs={24} lg={16}>
          <Steps current={Math.max(currentStepIndex, 0)} items={items} size="small" />
        </Col>
      </Row>

      {status.completed ? (
        <Alert
          style={{ marginTop: 16 }}
          type="success"
          showIcon
          message="Onboarding concluído"
          description="As principais configurações da conta já foram concluídas."
          action={
            <Button type="link" onClick={handleSkipOnboarding} loading={skippingOnboarding}>
              Ocultar card
            </Button>
          }
        />
      ) : currentStep && (
        <Alert
          style={{ marginTop: 16 }}
          type="info"
          showIcon
          message={currentStep.label}
          description={currentStep.description}
          action={
            <Space wrap>
              <Button ref={startStepButtonRef} type="primary" onClick={() => handleNavigate(currentStep)}>
                Começar etapa
              </Button>
              <Button onClick={() => handleSkipStep(currentStep)} loading={skippingStep}>
                Ignorar etapa
              </Button>
              <Button type="link" onClick={handleSkipOnboarding} loading={skippingOnboarding}>
                Ocultar card
              </Button>
            </Space>
          }
        />
      )}
    </Card>
  );
};

export default OnboardingProgressCard;
